import React, { useState, useRef, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { fetchDashboard, fetchUsers, fetchDepartments } from '../../../redux/actions';
import { BsEnvelope } from 'react-icons/bs';
import DashboardFilterDropdown from './DashboardFilterDropdown';
import DashboardDateRangeDropdown, { getPresetRange } from './DashboardDateRangeDropdown';
import SummaryEmailModal from '../../../modals/Tickets/SummaryEmailModal';

const DashboardFilters = ({ isLoading, onFiltersActive, onAnyOpen }) => {
	const dispatch = useDispatch();
	const allUsers = useSelector(state => state?.usersReducer.users ?? []);
	const allDepartments = useSelector(state => state?.departmentsReducer.departments ?? []);
	const currentUser = useSelector(state => state?.userReducer?.user ?? null);

	const [selectedUsers, setSelectedUsers] = useState([]);
	const [selectedDepartments, setSelectedDepartments] = useState([]);
	const [dateFrom, setDateFrom] = useState(() => getPresetRange('this_month')[0]);
	const [dateTo, setDateTo] = useState(() => getPresetRange('this_month')[1]);
	const [activePreset, setActivePreset] = useState('this_month');
	const [initialised, setInitialised] = useState(false);
	const [userHasFiltered, setUserHasFiltered] = useState(false);
	const [showSummaryModal, setShowSummaryModal] = useState(false);
	const [usersOpen, setUsersOpen] = useState(false);
	const [depsOpen, setDepsOpen] = useState(false);
	const [dateOpen, setDateOpen] = useState(false);
	const deliberateCloseRef = useRef(false);
	const outsideCloseRef = useRef(false);

	useEffect(() => {
		const anyOpen = usersOpen || depsOpen || dateOpen;
		if (anyOpen) {
			onAnyOpen?.(true);
			deliberateCloseRef.current = false;
			outsideCloseRef.current = false;
		} else if (deliberateCloseRef.current) {
			// Toggle button / option selection explicitly closed the last filter
			onAnyOpen?.(false);
			deliberateCloseRef.current = false;
			outsideCloseRef.current = false;
		} else {
			// Outside-click closed the last filter — could be a transition to another
			// filter or a genuine dismiss. Resolve on the next click event.
			outsideCloseRef.current = true;
		}
	}, [usersOpen, depsOpen, dateOpen]);

	// After an outside-close, the next click tells us intent:
	// - landed on a filter toggle → transition, keep sticky
	// - landed anywhere else      → genuine dismiss, clear sticky
	useEffect(() => {
		const handleClick = (e) => {
			if (!outsideCloseRef.current) return;
			outsideCloseRef.current = false;
			if (!e.target.closest('[data-filter-toggle]')) {
				onAnyOpen?.(false);
			}
		};
		document.addEventListener('click', handleClick);
		return () => document.removeEventListener('click', handleClick);
	}, []);

	const handleUsersOpenChange = (open, reason) => {
		setUsersOpen(open);
		if (!open && reason === 'deliberate') deliberateCloseRef.current = true;
	};

	const handleDepsOpenChange = (open, reason) => {
		setDepsOpen(open);
		if (!open && reason === 'deliberate') deliberateCloseRef.current = true;
	};

	const handleDateOpenChange = (open, reason) => {
		setDateOpen(open);
		if (!open && reason === 'deliberate') deliberateCloseRef.current = true;
	};

	useEffect(() => {
		dispatch(fetchUsers());
		dispatch(fetchDepartments());
	}, [dispatch]);

	// Once currentUser is available (Redux hydrated), pre-select them and fetch
	useEffect(() => {
		if (currentUser?.id && !initialised) {
			const [from, to] = getPresetRange('this_month');
			setSelectedUsers([currentUser.id]);
			setInitialised(true);
			const params = new URLSearchParams();
			params.set('assignee', currentUser.id);
			params.set('created_at', 'range');
			params.set('created_at_range', `${from},${to}`);
			dispatch(fetchDashboard(params.toString()));
		}
	}, [currentUser?.id, initialised, dispatch]);

	const buildQueryAndFetch = ({ users, departments, from, to }) => {
		const params = new URLSearchParams();
		if (users?.length) params.set('assignee', users.join(','));
		if (departments?.length) params.set('department', departments.join(','));
		if (from && to) {
			params.set('created_at', 'range');
			params.set('created_at_range', `${from},${to}`);
		}
		dispatch(fetchDashboard(params.toString()));
	};

	const handleUsersChange = (key, value) => {
		const users = value != null ? [value] : [];
		setSelectedUsers(users);
		setUserHasFiltered(true);
		buildQueryAndFetch({ users, departments: selectedDepartments, from: dateFrom, to: dateTo });
	};

	const handleDepartmentsChange = (key, value) => {
		setSelectedDepartments(value);
		setUserHasFiltered(true);
		buildQueryAndFetch({ users: selectedUsers, departments: value, from: dateFrom, to: dateTo });
	};

	const handleDateChange = ({ dateFrom: from, dateTo: to, activePreset: preset }) => {
		setDateFrom(from);
		setDateTo(to);
		setActivePreset(preset);
		setUserHasFiltered(true);
		buildQueryAndFetch({ users: selectedUsers, departments: selectedDepartments, from, to });
	};

	const handleClearAll = () => {
		setSelectedUsers([]);
		setSelectedDepartments([]);
		setDateFrom(null);
		setDateTo(null);
		setActivePreset(null);
		setUserHasFiltered(true);
		dispatch(fetchDashboard());
	};

	const userOptions = allUsers.map(u => ({
		value: u.id,
		label: u.name,
		image: u.profile_image,
		description: u.email,
	}));

	const departmentOptions = allDepartments.map(d => ({
		value: d.id,
		label: d.name,
		color: d.color,
	}));

	const hasAnyFilter = selectedUsers.length > 0 || selectedDepartments.length > 0 || dateFrom || dateTo;

	useEffect(() => {
		onFiltersActive?.(userHasFiltered);
	}, [userHasFiltered]);

	return (
		<div className="d-flex align-items-center justify-content-end" style={{ gap: '8px', flexWrap: 'wrap' }}>
			<DashboardFilterDropdown
				label="Users"
				filterKey="users"
				options={userOptions}
				value={selectedUsers}
				onChange={handleUsersChange}
				isLoading={isLoading}
				single
				onOpenChange={handleUsersOpenChange}
			/>
			<DashboardFilterDropdown
				label="Departments"
				filterKey="departments"
				options={departmentOptions}
				value={selectedDepartments}
				onChange={handleDepartmentsChange}
				isLoading={isLoading}
				onOpenChange={handleDepsOpenChange}
			/>
			<DashboardDateRangeDropdown
				dateFrom={dateFrom}
				dateTo={dateTo}
				activePreset={activePreset}
				onChange={handleDateChange}
				isLoading={isLoading}
				onOpenChange={handleDateOpenChange}
			/>
			{hasAnyFilter && (
				<button
					className="btn btn-sm btn-link p-0 quick-filter-clear-btn"
					style={{ fontSize: '14px', whiteSpace: 'nowrap', alignSelf: 'center' }}
					onClick={handleClearAll}
					disabled={isLoading}
				>
					Clear All
				</button>
			)}
			<button
				type="button"
				className="btn btn-secondary d-flex align-items-center gap-1"
				style={{ fontSize: '14px', whiteSpace: 'nowrap', overflow: 'visible' }}
				onClick={() => setShowSummaryModal(true)}
				disabled={isLoading}
				title="Email summary"
			>
				<BsEnvelope className="flex-shrink-0" size={18} aria-hidden />
				<span className="flex-shrink-0">Email Summary</span>
			</button>
			{showSummaryModal && (
				<SummaryEmailModal
					closeModal={() => setShowSummaryModal(false)}
					initialDepartments={selectedDepartments}
					initialStatuses={[]}
					initialDateFrom={dateFrom ?? null}
					initialDateTo={dateTo ?? null}
				/>
			)}
		</div>
	);
};

export default DashboardFilters;
